import React, { useCallback, useMemo, useRef, useState } from 'react'
import VersatileGrid, { Header } from '../../ui_reusable/VersatileGrid'
import {
  VerySmallNormalTag,
  VerySmallErrorTag,
} from '../../ui_reusable/styles/Tags.styles'
import { EasyIconButton } from '../../ui_reusable/styles/Button.styles'
import CustomIcons from '../../ui_reusable/CustomIcons'
import theme from '../../../styles/theme'
import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'
import { SmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import { StandardCheckbox } from '../../ui_reusable/StandardInputs'
import ShowDatasetDropdown from '../datasets/ShowDatasetDropdown'
import { HeaderCellFont } from '../../ui_reusable/styles/VersatileGrid.styles'
import { Tooltip, Whisper } from 'rsuite'
import { getQualityTagByQualityString } from '../../../utils/ScoreFitnessValidation'
import { GridCellBoldText } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import {
  DriverNameContainer,
  ActionsContainer,
  TooltipWrapper,
  GridCellBoldTextInvalid,
  StashedDriverTag,
  ConditionalGridCellNormalText,
  ConditionalGridCellSmallFadedText,
} from '../../models/build_model_page/build_model/styles/BuildModelDriverList.styles'
import { ProjectSolutionDriver } from '@/api/generated/models'
import {
  StashedDriver,
  getStashReasonTooltip,
} from '../../models/build_model_page/build_model/types/StashedDriver'
import DriverSelectionFilter from './DriverSelectionFilter'

// Define the props interface for the component
interface SelectableDriverListProps {
  data: ProjectSolutionDriver[]
  loading?: boolean
  selectedDrivers: number[]
  dependentDriverId: number | null | undefined
  expandedRows: Set<number>
  onExpandRow: (dataIdx: number) => void
  totalAvailable: number
  totalSelected: number
  onSelectedChange: (selectedIds: number[]) => void
  stashedDrivers: StashedDriver[]
  useRole?: boolean
  /** Page size for VersatileGrid (client or server mode). */
  pageSize?: number
  /** Opt-in server-side pagination (caller owns fetch). */
  serverSide?: boolean
  serverPage?: number
  onPageChange?: (page: number) => void
  onSearchChange?: (term: string) => void
  onSortChange?: (
    sort: { key: string; direction: 'asc' | 'desc' } | null,
  ) => void
  onSelectAllToggle?: () => void
  /** Checked state for the header select-all when `onSelectAllToggle` is set. */
  allSelected?: boolean
  showSelectedOnly?: boolean
  onShowSelectedChange?: (showSelectedOnly: boolean) => void
  availableFirst?: boolean
  onAvailableFirstChange?: (availableFirst: boolean) => void
}

// SelectAllCheckbox component with ref forwarding
const SelectAllCheckbox = React.memo(
  React.forwardRef<
    HTMLDivElement,
    { allSelected: boolean; onSelectAll: () => void; disabled?: boolean }
  >(({ allSelected, onSelectAll, disabled = false }, ref) => {
    return (
      <div
        ref={ref}
        style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
      >
        <StandardCheckbox
          onChange={onSelectAll}
          checked={allSelected}
          disabled={disabled}
        />
        <span css={HeaderCellFont}>Select all/none</span>
      </div>
    )
  }),
)

// DisabledTooltipWrapper component for wrapping cells with tooltips when disabled
const DisabledTooltipWrapper: React.FC<{
  children: React.ReactElement
  isDisabled: boolean
  tooltipText: string
}> = ({ children, isDisabled, tooltipText }) => {
  if (!isDisabled) {
    return children
  }

  return (
    <Whisper
      placement="topStart"
      trigger="hover"
      speaker={
        <Tooltip
          style={{
            backgroundColor: theme.colors.background,
            color: theme.colors.veryVeryFadedText,
            outline: '1px solid',
            whiteSpace: 'pre-wrap',
            width: 'auto',
            maxWidth: 'none',
          }}
        >
          {tooltipText}
        </Tooltip>
      }
    >
      <TooltipWrapper>{children}</TooltipWrapper>
    </Whisper>
  )
}

const SelectableDriverList: React.FC<SelectableDriverListProps> = ({
  data = [],
  loading = false,
  selectedDrivers = [],
  dependentDriverId = null,
  expandedRows = new Set(),
  onExpandRow,
  totalAvailable = 0,
  totalSelected = 0,
  onSelectedChange = () => {},
  stashedDrivers = [],
  useRole = false,
  pageSize = 10,
  serverSide = false,
  serverPage = 1,
  onPageChange,
  onSearchChange,
  onSortChange,
  onSelectAllToggle,
  allSelected: allSelectedProp,
  showSelectedOnly: showSelectedOnlyProp,
  onShowSelectedChange,
  availableFirst = false,
  onAvailableFirstChange,
}) => {
  const [internalShowSelectedOnly, setInternalShowSelectedOnly] =
    useState(false)
  const isSelectedFilterControlled = typeof onShowSelectedChange === 'function'
  const showSelectedOnly = isSelectedFilterControlled
    ? Boolean(showSelectedOnlyProp)
    : internalShowSelectedOnly

  const handleShowSelectedChange = useCallback(
    (next: boolean) => {
      if (isSelectedFilterControlled) {
        onShowSelectedChange?.(next)
      } else {
        setInternalShowSelectedOnly(next)
      }
    },
    [isSelectedFilterControlled, onShowSelectedChange],
  )
  const getHighlightType = useCallback(
    (driver_type: string, isActive?: boolean) => {
      if (isActive === false) {
        return 'invalid_from_server'
      }
      return driver_type
    },
    [],
  )

  const flattenedData = useMemo(() => {
    return data.map((item, index) => {
      const stashedInfo = stashedDrivers.find(
        (s) => s.id === item.driver?.id,
      )
      let driver_type = ''
      if (item.driver?.id === dependentDriverId) {
        driver_type = 'outcome'
      } else if (selectedDrivers.includes(item.driver?.id ?? -1)) {
        driver_type = 'explanatory'
      } else if (stashedInfo) {
        driver_type = 'stashed'
      } else {
        driver_type = 'unselected'
      }

      const highlightType = getHighlightType(
        driver_type,
        // @ts-ignore
        item.driver?.is_active,
      )

      return {
        id: item.driver?.id,
        name: item.driver?.name,
        source: item.driver?.source,
        updated_at: item.driver?.updated_at,
        interval: item.driver?.interval,
        total_observations: item.driver?.total_observations,
        units: item.driver?.units,
        quality: item.driver?.quality,
        is_active:
          // @ts-ignore
          item.driver?.is_active !== undefined ? item.driver?.is_active : true,
        // @ts-ignore
        comment: item.driver?.comment,
        driverId: item.driver?.id,
        originalItemId: item.id,
        _originalData: item,
        driver_type: driver_type,
        highlightType: highlightType,
        _searchId: `driver-${item.driver?.id || index}`,
        _originalIndex: index,
        stashReason: stashedInfo?.reason,
      }
    })
  }, [
    data,
    dependentDriverId,
    selectedDrivers,
    stashedDrivers,
    getHighlightType,
  ])

  const visibleData = useMemo(() => {
    const rows = showSelectedOnly
      ? flattenedData.filter(
          (driver) =>
            selectedDrivers.includes(driver.id ?? -1) ||
            driver.id === dependentDriverId,
        )
      : flattenedData
    if (!availableFirst) return rows
    return [...rows].sort((a, b) => {
      const aDisabled =
        a.is_active === false ||
        a.id === dependentDriverId ||
        a.stashReason !== undefined
      const bDisabled =
        b.is_active === false ||
        b.id === dependentDriverId ||
        b.stashReason !== undefined
      return Number(aDisabled) - Number(bDisabled)
    })
  }, [
    flattenedData,
    showSelectedOnly,
    selectedDrivers,
    dependentDriverId,
    availableFirst,
  ])

  const originalItemMap = useMemo(() => {
    const map = new Map<number, ProjectSolutionDriver>()
    data.forEach((item) => {
      if (item.driver?.id) {
        map.set(item.driver.id, item)
      }
    })
    return map
  }, [data])

  const filteredDriverIdsRef = useRef(new Set<number>())
  const selectAllCheckboxRef = useRef<HTMLDivElement>(null)

  const handleCheckboxClick = (driverId: number) => {
    if (!dependentDriverId) return
    // Note: Stashed drivers are cleared by the parent's onSelectedChange callback.
    // If a driver is truly incompatible (is_active === false), it will remain
    // disabled via the isDisabled logic in renderRow.
    const updatedSelection = selectedDrivers.includes(driverId)
      ? selectedDrivers.filter((id) => id !== driverId)
      : [...selectedDrivers, driverId]
    onSelectedChange(updatedSelection)
  }

  const getSelectableDrivers = useCallback(() => {
    return visibleData.filter(
      (d) => d.id !== dependentDriverId && d.is_active !== false,
    )
  }, [visibleData, dependentDriverId])

  const getFilteredSelectableDrivers = useCallback(() => {
    if (filteredDriverIdsRef.current.size === 0) return getSelectableDrivers()
    return getSelectableDrivers().filter((driver) =>
      filteredDriverIdsRef.current.has(driver.id ?? -1),
    )
  }, [getSelectableDrivers])

  const handleFilteredDataChange = useCallback(
    (filteredData: any[]) => {
      filteredDriverIdsRef.current = new Set(
        filteredData.map((item) => item.id),
      )
      const filteredSelectableDrivers = getFilteredSelectableDrivers()
      const newAllFilteredSelected =
        filteredSelectableDrivers.length > 0 &&
        filteredSelectableDrivers.every((driver) =>
          selectedDrivers.includes(driver.id ?? -1),
        )
      if (selectAllCheckboxRef.current) {
        const checkbox = selectAllCheckboxRef.current.querySelector(
          'input[type="checkbox"]',
        ) as HTMLInputElement | null
        if (checkbox) {
          checkbox.checked = newAllFilteredSelected
        }
      }
    },
    [getFilteredSelectableDrivers, selectedDrivers],
  )

  const handleSelectAll = () => {
    if (!dependentDriverId) return

    if (onSelectAllToggle) {
      onSelectAllToggle()
      return
    }

    const filteredSelectableDrivers = getFilteredSelectableDrivers()
    const allSelected =
      filteredSelectableDrivers.length > 0 &&
      filteredSelectableDrivers.every((driver) =>
        selectedDrivers.includes(driver.id ?? -1),
      )

    let updatedSelection
    if (allSelected) {
      updatedSelection = selectedDrivers.filter(
        (id) => !filteredSelectableDrivers.some((driver) => driver.id === id),
      )
    } else {
      const existingSelections = new Set(selectedDrivers)
      filteredSelectableDrivers.forEach((driver) => {
        if (driver.id) {
          existingSelections.add(driver.id)
        }
      })
      updatedSelection = Array.from(existingSelections)
    }
    onSelectedChange(updatedSelection)
  }

  const allFilteredSelected = onSelectAllToggle
    ? Boolean(allSelectedProp)
    : (() => {
        const filteredSelectableDrivers = getFilteredSelectableDrivers()
        return (
          filteredSelectableDrivers.length > 0 &&
          filteredSelectableDrivers.every((driver) =>
            selectedDrivers.includes(driver.id ?? -1),
          )
        )
      })()

  const isDriverStashed = useCallback(
    (driverId: number) => {
      return stashedDrivers.some((s) => s.id === driverId)
    },
    [stashedDrivers],
  )

  const selectionRequiresOutcome = !dependentDriverId

  const headers: Header[] = [
    {
      title: 'Driver Name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
      style: { flexGrow: 2 },
      overrideJSX: (
        <SelectAllCheckbox
          ref={selectAllCheckboxRef}
          allSelected={allFilteredSelected}
          onSelectAll={handleSelectAll}
          disabled={selectionRequiresOutcome}
        />
      ),
    },
    ...(useRole
      ? [
          {
            title: 'Role',
            key: 'driver_type',
            type: 'string' as const,
            // Role is derived client-side; not sortable on the server list.
            isSortable: !serverSide,
            isSearchable: true,
            center: true,
            getSortValue: (row: any) => {
              const sortOrderMap: { [key: string]: number } = {
                outcome: 1,
                explanatory: 2,
                stashed: 3,
                unselected: 4,
              }
              return sortOrderMap[row.driver_type] || 5
            },
            valueMapping: {
              outcome: 'Outcome',
              explanatory: 'Explanatory',
              stashed: 'Was stashed',
              unselected: '',
            },
          },
        ]
      : []),
    {
      title: 'Source',
      key: 'source',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'source',
    },
    {
      title: 'Last updated',
      key: 'updated_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: true,
      serverKey: 'updated_at',
    },
    {
      title: 'Granularity',
      key: 'interval',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'interval',
    },
    {
      title: 'Observations',
      key: 'total_observations',
      type: 'number',
      isSortable: true,
      isSearchable: true,
      serverKey: 'total_observations',
      center: true,
    },
    {
      title: 'Measure',
      key: 'units',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'units',
      center: true,
    },
    {
      title: 'Quality',
      key: 'quality',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'quality',
      center: true,
    },
    {
      title: 'View',
      key: 'actions',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
  ]

  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>
        Available Drivers: {totalAvailable}
      </span>
      <div>
        <SmallNormalTag>{totalSelected} drivers selected</SmallNormalTag>
      </div>
      <DriverSelectionFilter
        showSelectedOnly={showSelectedOnly}
        onChange={handleShowSelectedChange}
        availableFirst={availableFirst}
        onAvailableFirstChange={onAvailableFirstChange}
      />
    </StatsContainer>
  )

  const renderIntersectingRow = useCallback(
    (rowData: any, rowIdx: number, dataIdx: number) => {
      if (!expandedRows.has(dataIdx)) return null
      const originalItem = originalItemMap.get(rowData.id)
      if (!originalItem || !originalItem.driver) return null

      return (
        <ShowDatasetDropdown
          dataset={originalItem.driver}
          datasetId={originalItem.driver.id ?? -1}
          units={originalItem.driver.units}
        />
      )
    },
    [expandedRows, originalItemMap],
  )

  const renderCheckbox = (
    rowData: any,
    isDisabled: boolean,
  ) => {
    return (
      <StandardCheckbox
        onChange={() => (isDisabled ? {} : handleCheckboxClick(rowData.id))}
        disabled={isDisabled}
        checked={selectedDrivers.includes(rowData.id)}
      />
    )
  }

  // Helper: indent every line of backend content with the given prefix
  const indentContent = useCallback((content: string, indent: string): string => {
    return content
      .split('\n')
      .map((line) => `${indent}${line}`)
      .join('\n')
  }, [])

  // Format driver comment for tooltip display
  const formatDriverComment = useCallback((comment: string | undefined | null, driverName: string): string => {
    if (!comment?.trim()) {
      return 'Something is wrong with this driver'
    }
    // Driver name as header, then indent all backend content
    return `${driverName}:\n${indentContent(comment.trim(), '\t')}`
  }, [indentContent])

  return (
    <VersatileGrid
      headers={headers}
      data={visibleData}
      searchPlaceholder="Find driver"
      renderRow={(rowData: any, rowIdx: number, dataIdx: number) => {
        const isStashed = rowData.stashReason !== undefined
        const isDisabled =
          selectionRequiresOutcome ||
          dependentDriverId === rowData.id ||
          rowData.is_active === false ||
          isStashed

        // Determine tooltip text based on the reason for being disabled
        let tooltipText = ''
        if (selectionRequiresOutcome) {
          tooltipText = 'Select an outcome variable first'
        } else if (rowData.is_active === false) {
          tooltipText = formatDriverComment(rowData?.comment, rowData.name)
        } else if (dependentDriverId === rowData.id) {
          tooltipText = 'Already selected as dependent variable'
        } else if (rowData.stashReason) {
          tooltipText = getStashReasonTooltip(rowData.stashReason)
        }

        const isInvalid = rowData.highlightType === 'invalid_from_server'

        return [
          <DisabledTooltipWrapper
            isDisabled={isDisabled}
            tooltipText={tooltipText}
          >
            <DriverNameContainer>
              {renderCheckbox(rowData, isDisabled)}
              {isInvalid ? (
                <GridCellBoldTextInvalid>
                  {rowData.name}
                </GridCellBoldTextInvalid>
              ) : (
                <GridCellBoldText>{rowData.name}</GridCellBoldText>
              )}
            </DriverNameContainer>
          </DisabledTooltipWrapper>,
          ...(useRole
            ? [
                <ConditionalGridCellNormalText isInvalid={isInvalid}>
                  {dependentDriverId === rowData.id ? (
                    <VerySmallNormalTag>Outcome</VerySmallNormalTag>
                  ) : selectedDrivers.includes(rowData.id) ? (
                    <VerySmallNormalTag>Explanatory</VerySmallNormalTag>
                  ) : isDriverStashed(rowData.id) ? (
                    <VerySmallErrorTag css={StashedDriverTag}>
                      Was unselected!
                    </VerySmallErrorTag>
                  ) : null}
                </ConditionalGridCellNormalText>,
              ]
            : []),
          <ConditionalGridCellNormalText isInvalid={isInvalid}>
            {rowData.source}
          </ConditionalGridCellNormalText>,
          <ConditionalGridCellSmallFadedText isInvalid={isInvalid}>
            {rowData.updated_at
              ? `${new Date(rowData.updated_at).toISOString().slice(0, 10)}`
              : ''}
          </ConditionalGridCellSmallFadedText>,
          <ConditionalGridCellNormalText isInvalid={isInvalid}>
            {rowData.interval}
          </ConditionalGridCellNormalText>,
          <ConditionalGridCellNormalText isInvalid={isInvalid}>
            {rowData.total_observations}
          </ConditionalGridCellNormalText>,
          <ConditionalGridCellNormalText isInvalid={isInvalid}>
            {rowData.units}
          </ConditionalGridCellNormalText>,
          <ConditionalGridCellNormalText isInvalid={isInvalid}>
            {getQualityTagByQualityString(rowData.quality)}
          </ConditionalGridCellNormalText>,
          <ActionsContainer>
            <EasyIconButton onClick={() => onExpandRow(dataIdx)}>
              {expandedRows.has(dataIdx) ? (
                <CustomIcons.faEyeSlash
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              ) : (
                <CustomIcons.faEye
                  color={theme.colors.veryFadedText}
                  solid={false}
                  size="xs"
                />
              )}
            </EasyIconButton>
          </ActionsContainer>,
        ]
      }}
      renderIntersectingRow={renderIntersectingRow}
      maxDisplayedItems={pageSize}
      headerInfo={gridHeader}
      loading={loading}
      loadingMessage={'Loading datasets...'}
      serverSide={serverSide}
      serverTotal={serverSide ? totalAvailable : undefined}
      serverPage={serverPage}
      onPageChange={onPageChange}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      highlightDataIdx={(() => {
        const highlights: { idx: number; color: string }[] = []
        visibleData.forEach((driver, index) => {
          if (driver.highlightType === 'outcome') {
            highlights.push({
              idx: index,
              color: '#FFF8E6',
            })
          } else if (driver.highlightType === 'invalid_from_server') {
            highlights.push({
              idx: index,
              color: theme.colors.background,
            })
          } else if (driver.highlightType === 'stashed') {
            highlights.push({
              idx: index,
              color: theme.colors.background,
            })
          }
        })
        return highlights.length > 0 ? highlights : null
      })()}
      onFilteredDataChange={serverSide ? undefined : handleFilteredDataChange}
    />
  )
}

export default SelectableDriverList
